import { canReadDomain } from '@/api/core/authorization';
import {
  evaluateLongMonth,
  ownLongPolicy,
  readLongMonthPricing,
  sendLongMonthCommand,
  type LongMonthCommand,
  type LongMonthEvaluation,
  type LongPricingKind,
  type LongPricingRead,
  type LongPricingTarget,
  type LongPricingValues,
} from '@/api/core/native-long-month-pricing';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import { DirtyGuard } from '@/components/dirty-guard';
import { Button, DatePicker, Input } from '@mgrvtechgroup/admin-ds';
import { useEffect, useRef, useState } from 'react';
import { EditActionBar } from './edit-action-bar';
import { LongMonthRulesPanel } from './native-long-month-rules';
import { LongMonthTiersPanel } from './native-long-month-tiers';
import { useCurrentCommand } from './presentation/use-current-command';
import type { PropertyRuntime } from './property-queries';

const LEVEL: Record<LongPricingKind, string> = {
  'room-types': '객실 유형',
  rooms: '객실',
  units: '슬롯',
};
const won = (v: number | null) =>
  v === null ? '직접 설정 안 함' : `${v.toLocaleString('ko-KR')}원`;
const FIELDS = [
  ['regular_amount_minor', '월 정가'],
  ['deposit_amount', '보증금'],
  ['cleaning_fee', '청소비'],
] as const;
type Draft = Record<(typeof FIELDS)[number][0], string>;
/** 판매 탭은 숨겨 둔 채 입력을 보존하지만, 객실·슬롯·유형을 바꾸면 이 폼이 새로 만들어져 입력이 사라진다. */
const selectionChanged = (a: { search: string }, b: { search: string }) => {
  const x = new URLSearchParams(a.search);
  const y = new URLSearchParams(b.search);
  return ['type', 'room', 'slot'].some((key) => x.get(key) !== y.get(key));
};
const toDraft = (v: LongPricingValues | null): Draft => ({
  regular_amount_minor: v?.regular_amount_minor?.toString() ?? '',
  deposit_amount: v?.deposit_amount?.toString() ?? '',
  cleaning_fee: v?.cleaning_fee?.toString() ?? '',
});
function parseDraft(draft: Draft, kind: LongPricingKind): LongPricingValues | string {
  const out: Record<string, number | null> = {};
  for (const [key, label] of FIELDS) {
    const raw = draft[key].trim();
    if (raw === '') {
      if (kind === 'room-types') return `${label}은(는) 객실 유형에서 비워 둘 수 없습니다.`;
      out[key] = null;
      continue;
    }
    if (!/^\d{1,15}$/.test(raw)) return `${label}은(는) 0 이상의 정수로 입력해 주세요.`;
    const n = Number(raw);
    if (key === 'regular_amount_minor' && n <= 0) return '월 정가는 0원일 수 없습니다.';
    out[key] = n;
  }
  return out as unknown as LongPricingValues;
}
const sameValues = (own: LongPricingValues | null, sent: LongPricingValues | null) =>
  !!own && !!sent && FIELDS.every(([key]) => own[key] === sent[key]);
function message(error: unknown) {
  if (isApiError(error)) {
    if (error.status === 403)
      return '이 작업을 할 권한이 없습니다. 조회와 수정 권한은 따로 부여됩니다.';
    if (error.status === 404) return '대상을 찾지 못했습니다.';
    if (error.status === 409)
      return '그사이 값이 바뀌었습니다. 현재 값을 다시 조회했으니 확인 후 다시 입력해 주세요.';
    if (error.status === 422) return '입력값을 확인해 주세요.';
    if (error.status === 503) return '지금 확인할 수 없습니다. 잠시 후 다시 시도해 주세요.';
  }
  return '요청 결과를 확인하지 못했습니다.';
}

/** 선택한 대상의 장기 월 가격: 직접 설정한 값을 있는 그대로 보여 주고, 실효값·출처는 서버 계산 결과로만 보여 준다. */
export function LongMonthPricingSection({
  readEnabled = true,
  runtime,
  generation,
  kind,
  id,
  showCommonTiers = true,
}: {
  readEnabled?: boolean;
  runtime: PropertyRuntime;
  generation: number;
  kind: LongPricingKind;
  id: string;
  showCommonTiers?: boolean;
}) {
  const session = useOperatorSession(runtime.session);
  const allowedBits = (bits: number) =>
    session.status === 'authorized' &&
    !session.deniedDomains.includes('booking') &&
    !!session.authorization &&
    canReadDomain(session.authorization, 'booking', bits);
  const canEdit = allowedBits(1);
  const canQuote = allowedBits(2);
  const target = { kind, id };
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(toDraft(null));
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const discardDraft = () => {
    setEditing(false);
    setConfirm(false);
    setReason('');
  };
  // 현재 작업자(users/me)·예약 영역 권한·같은 명령 UUID/본문 재전송·거절 시 폐기는 공유 명령 훅이 맡는다.
  const cmd = useCurrentCommand<
    { target: LongPricingTarget; read: LongPricingRead },
    LongMonthCommand
  >({
    runtime,
    scope: `long-month:${kind}:${id}`,
    readEnabled,
    readKey: String(generation),
    bits: 1,
    domain: 'booking',
    read: async () => ({ target, read: await readLongMonthPricing(runtime.client, target) }),
    write: (command) => sendLongMonthCommand(runtime.client, target, command),
    validate: () => Promise.resolve(),
    onActorChanged: discardDraft,
  });
  // 훅 상태는 대상이 바뀐 직후 한 번 이전 대상의 값을 들고 있을 수 있어 같은 대상일 때만 쓴다.
  const read: LongPricingRead | undefined =
    cmd.current?.target.kind === kind && cmd.current.target.id === id
      ? cmd.current.read
      : undefined;
  const own = read ? ownLongPolicy(read, target) : null;
  const unknownCommand =
    cmd.attempt && !cmd.attempt.accepted ? (cmd.attempt.body as LongMonthCommand) : null;
  const pending = unknownCommand?.op === 'policy' ? unknownCommand.body : null;
  const sentValues = useRef<LongPricingValues | null>(null);
  const lastOp = useRef<LongMonthCommand['op']>('policy');
  // 저장이 받아들여지고 같은 대상을 다시 읽은 뒤에만 입력 화면을 닫는다.
  useEffect(() => {
    if (cmd.success) {
      setEditing(false);
      setConfirm(false);
    }
  }, [cmd.success]);
  // 서버가 거절·권한 상실로 현재 값을 버리면 이전 초안도 함께 버린다.
  useEffect(() => {
    if (!cmd.current && !cmd.attempt) discardDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmd.current, cmd.attempt]);
  const [evaluation, setEvaluation] = useState<LongMonthEvaluation | null>(null);
  const [quote, setQuote] = useState({ first_date: '', term_value: '1' });
  const [quoteError, setQuoteError] = useState('');

  if (!canEdit)
    return (
      <section aria-label="장기 월 가격" className="rounded-md border p-4">
        <h3 className="font-semibold">{LEVEL[kind]} 장기 월 가격 (원/월)</h3>
        <p className="body-sm text-muted-foreground mt-2" role="status">
          장기 월 가격을 보려면 예약 영역의 수정 권한이 필요합니다. 값이 없다는 뜻이 아닙니다.
        </p>
      </section>
    );

  const parsed = parseDraft(draft, kind);
  const dirty =
    editing &&
    !pending &&
    (reason !== '' || JSON.stringify(draft) !== JSON.stringify(toDraft(own)));
  const submit = () => {
    if (!read || typeof parsed === 'string' || cmd.busy) return;
    lastOp.current = 'policy';
    sentValues.current = pending?.values ?? parsed;
    void cmd.submit({
      op: 'policy',
      body: {
        command_id: crypto.randomUUID(),
        reason: reason.trim(),
        expected: read.cas,
        values: parsed,
      },
    });
  };
  const runQuote = async () => {
    if (kind === 'room-types') return;
    setQuoteError('');
    setEvaluation(null);
    try {
      setEvaluation(
        await evaluateLongMonth(
          runtime.client,
          { kind, id },
          {
            applies_to: 'NEW',
            first_date: quote.first_date,
            term_unit: 'MONTH',
            term_value: Number(quote.term_value),
          },
        ),
      );
    } catch (e) {
      setQuoteError(
        isApiError(e) && e.status === 403
          ? '월 가격 계산을 볼 권한이 없습니다. 가격이 없다는 뜻이 아닙니다.'
          : isApiError(e) && e.status === 422
            ? '이 조건으로는 서버가 가격을 계산하지 않았습니다. 미계산이며 0원이 아닙니다.'
            : message(e),
      );
    }
  };
  const sourceLabel = (policyId: string | null, reasonCode: string | null) => {
    if (!policyId)
      return reasonCode === 'TYPE_POLICY_ABSENT' ? '객실 유형 가격 없음' : '출처 확인 불가';
    const p = read?.policies.find((x) => x.id === policyId);
    return p
      ? `${LEVEL[p.target_kind === 'TYPE' ? 'room-types' : p.target_kind === 'ROOM' ? 'rooms' : 'units']} 값`
      : '출처 확인 불가';
  };

  return (
    <section
      aria-label="장기 월 가격"
      className="body-sm space-y-4 rounded-md border p-4 leading-relaxed"
    >
      <DirtyGuard when={dirty} leaves={selectionChanged} />
      <h3 className="font-semibold">{LEVEL[kind]} 장기 월 가격 (원/월)</h3>
      {!read && !cmd.error && <p role="status">장기 월 가격을 불러오고 있습니다.</p>}
      {cmd.error && !editing && (
        <p role="alert" className="body-sm">
          {cmd.error}
        </p>
      )}
      {!read && cmd.error && (
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          onClick={() => {
            void cmd.refresh();
          }}
        >
          현재 값 다시 조회
        </Button>
      )}
      {read && (
        <>
          <p className="body-sm text-muted-foreground">
            {own
              ? `이 ${LEVEL[kind]}에 직접 설정한 값입니다. 비어 있는 항목은 직접 설정하지 않은 것이며 0원이 아닙니다.`
              : `이 ${LEVEL[kind]}에는 직접 설정한 월 가격이 없습니다. 아래에서 적용 가격을 계산해 확인하세요.`}
          </p>
          <dl className="body-sm grid gap-2 sm:grid-cols-3">
            {FIELDS.map(([key, label]) => (
              <div key={key}>
                <dt className="text-muted-foreground">{label}</dt>
                <dd>
                  {own ? (own[key] === 0 ? '0원 (면제로 명시)' : won(own[key])) : '직접 설정 안 함'}
                </dd>
              </div>
            ))}
          </dl>
          {cmd.success && lastOp.current === 'policy' && (
            <p role="status">
              {sameValues(own, sentValues.current)
                ? '저장 후 같은 대상의 현재 값을 다시 조회해 입력한 값과 같음을 확인했습니다.'
                : '저장 후 다시 조회한 값이 입력과 다릅니다. 아래 현재 값을 확인해 주세요.'}
            </p>
          )}
          {cmd.success && lastOp.current !== 'policy' && (
            <p role="status">
              저장 후 같은 대상을 다시 조회했습니다. 위 현재 값과 아래 규칙이 서버의 현재
              상태입니다.
            </p>
          )}
          {unknownCommand && unknownCommand.op !== 'policy' && (
            <div className="space-y-2" aria-label="결과를 모르는 요청">
              <p className="body-sm">
                이전 요청의 결과를 확인하지 못했습니다. 같은 요청만 다시 보낼 수 있습니다.
              </p>
              <Button
                className="h-auto min-h-8 whitespace-normal"
                disabled={cmd.busy}
                onClick={() => {
                  void cmd.submit();
                }}
              >
                같은 요청 다시 보내기
              </Button>
            </div>
          )}
          {editing ? (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                {FIELDS.map(([key, label]) => (
                  <label key={key} className="grid gap-1">
                    <span className="body-sm">{label} (원)</span>
                    <Input
                      inputMode="numeric"
                      value={draft[key]}
                      disabled={cmd.busy || !!pending}
                      placeholder={kind === 'room-types' ? '' : '비우면 직접 설정 안 함'}
                      onChange={(e) => {
                        setDraft({ ...draft, [key]: e.target.value });
                        setConfirm(false);
                      }}
                    />
                  </label>
                ))}
              </div>
              <label className="grid gap-1">
                <span className="body-sm">변경 사유</span>
                <Input
                  value={reason}
                  disabled={cmd.busy || !!pending}
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                />
              </label>
              {typeof parsed === 'string' && (
                <p role="alert" className="body-sm">
                  {parsed}
                </p>
              )}
              {confirm ? (
                <div className="space-y-2" aria-label="장기 월 가격 변경 확인">
                  {typeof parsed !== 'string' && (
                    <ul className="body-sm list-disc pl-5">
                      {FIELDS.map(([key, label]) => (
                        <li key={key}>
                          {label}: {won(own?.[key] ?? null)} → {won(parsed[key])}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="body-sm text-muted-foreground">
                    저장하면 이 {LEVEL[kind]}의 장기 월 가격만 바뀌고, 이미 받은 예약 금액은 바뀌지
                    않습니다.
                  </p>
                  <EditActionBar label="장기 월 가격 저장 동작">
                    <Button
                      className="h-auto min-h-8 whitespace-normal"
                      disabled={cmd.busy}
                      onClick={submit}
                    >
                      {pending ? '같은 요청 다시 보내기' : '장기 월 가격 저장'}
                    </Button>
                  </EditActionBar>
                </div>
              ) : (
                <EditActionBar label="장기 월 가격 검토 동작">
                  <Button
                    className="h-auto min-h-8 whitespace-normal"
                    variant="outline"
                    disabled={typeof parsed === 'string' || !reason.trim() || cmd.busy}
                    onClick={() => {
                      setConfirm(true);
                    }}
                  >
                    변경 내용·영향 확인
                  </Button>
                </EditActionBar>
              )}
              {cmd.error && (
                <p role="alert" className="body-sm">
                  {cmd.error}
                </p>
              )}
              <Button
                className="h-auto min-h-8 whitespace-normal"
                variant="outline"
                disabled={cmd.busy || !!pending}
                onClick={() => {
                  setEditing(false);
                }}
              >
                수정 취소
              </Button>
            </div>
          ) : (
            <Button
              className="h-auto min-h-8 whitespace-normal"
              variant="outline"
              onClick={() => {
                // 결과를 모르는 요청이 남아 있으면 그 요청의 값·사유를 그대로 보여 주고 같은 요청만 다시 보낸다.
                setDraft(toDraft(pending?.values ?? own));
                setReason(pending?.reason ?? '');
                setConfirm(!!pending);
                setEditing(true);
              }}
            >
              장기 월 가격 수정
            </Button>
          )}
          <LongMonthRulesPanel
            read={read}
            own={own}
            busy={cmd.busy}
            pending={!!unknownCommand}
            submit={(command) => {
              lastOp.current = command.op;
              void cmd.submit(command);
            }}
          />
          {showCommonTiers && (
            <LongMonthTiersPanel
              readEnabled={readEnabled}
              runtime={runtime}
              propertyId={String(read.cas.property_id)}
              stateKey={JSON.stringify(read.cas)}
              onChanged={() => {
                void cmd.refresh();
              }}
            />
          )}
          {kind !== 'room-types' && (
            <div className="space-y-2 border-t pt-3" aria-label="적용 가격 서버 계산">
              <h4 className="font-medium">적용되는 가격 확인</h4>
              {canQuote ? (
                <>
                  <p className="body-sm text-muted-foreground">
                    어느 단계의 값이 적용되는지는 서버가 계산한 결과로만 보여 줍니다.
                  </p>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="body-sm grid gap-1">
                      입주 시작일
                      <DatePicker
                        value={
                          quote.first_date ? new Date(`${quote.first_date}T12:00:00`) : undefined
                        }
                        onChange={(date) => {
                          setQuote({
                            ...quote,
                            first_date: date
                              ? `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                              : '',
                          });
                        }}
                      />
                    </div>
                    <label className="grid gap-1">
                      <span className="body-sm">계약 개월 수</span>
                      <Input
                        inputMode="numeric"
                        wrapperClassName="w-24"
                        value={quote.term_value}
                        onChange={(e) => {
                          setQuote({ ...quote, term_value: e.target.value });
                        }}
                      />
                    </label>
                    <Button
                      className="h-auto min-h-8 whitespace-normal"
                      variant="outline"
                      disabled={
                        !/^\d{4}-\d{2}-\d{2}$/.test(quote.first_date) ||
                        !/^[1-9]\d{0,3}$/.test(quote.term_value)
                      }
                      onClick={() => void runQuote()}
                    >
                      적용 가격 계산
                    </Button>
                  </div>
                  {quoteError && (
                    <p role="alert" className="body-sm">
                      {quoteError}
                    </p>
                  )}
                  {evaluation && (
                    <div className="body-sm space-y-1" role="status">
                      <p>
                        월 {evaluation.monthly_effective_amount_minor.toLocaleString('ko-KR')}원 ·
                        완성 {evaluation.complete_months}개월 + {evaluation.remaining_days}일 ·
                        숙박료 {evaluation.accommodation_amount.toLocaleString('ko-KR')}원 · 보증금{' '}
                        {evaluation.deposit_amount.toLocaleString('ko-KR')}원 · 청소비{' '}
                        {evaluation.cleaning_fee.toLocaleString('ko-KR')}원 · 합계{' '}
                        {evaluation.total.toLocaleString('ko-KR')}원
                      </p>
                      <ul aria-label="적용된 값의 출처">
                        {FIELDS.map(([key, label]) => (
                          <li key={key}>
                            {label}:{' '}
                            {evaluation.field_sources[key].value === null
                              ? '계산되지 않음'
                              : `${evaluation.field_sources[key].value.toLocaleString('ko-KR')}원`}{' '}
                            ·{' '}
                            {sourceLabel(
                              evaluation.field_sources[key].source_policy_id,
                              evaluation.field_sources[key].reason,
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <p className="body-sm text-muted-foreground" role="status">
                  적용 가격 계산은 예약 영역의 조회 권한이 있을 때 볼 수 있습니다.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
